/**
 * Cart drawer + AJAX add-to-cart + cart note (measurements) handling.
 */

/**
 * Announce a message via the aria-live region so screen readers pick it up.
 * @param {string} msg
 */
function announceCart(msg) {
  const el = document.getElementById('CartAnnouncer');
  if (!el) return;
  // Clear and re-set to ensure the change is observed by the browser
  el.textContent = '';
  requestAnimationFrame(() => { el.textContent = msg; });
}
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) this.close();
    });
  }

  open() {
    this.hidden = false;
    // Lock page scroll — store Y position for iOS restoration
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.dataset.scrollY = scrollY;
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
  }

  close() {
    this.classList.remove('is-open');
    setTimeout(() => {
      this.hidden = true;
      // Restore page scroll position (iOS fix)
      const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      delete document.body.dataset.scrollY;
      window.scrollTo(0, scrollY);
    }, 350);
  }

  async refresh() {
    try {
      // Shopify Sections API (plural) returns JSON: { "cart-drawer": "<html string>" }
      const res  = await fetch(`${window.theme.routes.cart}?sections=cart-drawer`, { cache: 'no-store' });
      const data = await res.json();
      const html = data['cart-drawer'];
      if (html) {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
        if (fresh) {
          // Swap inner content only — open/closed state is preserved, no flicker
          this.innerHTML = fresh.innerHTML;
        }
      }
    } catch (err) {
      console.error('[cart-drawer] refresh failed', err);
    }
    // Notify other modules that the drawer refreshed
    document.dispatchEvent(new CustomEvent('cart:drawer-refreshed'));
  }
}
customElements.define('cart-drawer', CartDrawer);

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(e) {
    e.preventDefault();

    // "Buy it now" — add to cart then redirect straight to checkout, bypassing cart page.
    if (e.submitter && e.submitter.name === 'checkout') {
      const buyBtn = e.submitter;
      const originalText = buyBtn.textContent;
      buyBtn.disabled = true;
      buyBtn.textContent = '…';
      try {
        const formData = new FormData(this.form);
        const res = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData
        });
        if (!res.ok) throw new Error(await res.text());
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[buy-now] failed', err);
        buyBtn.disabled = false;
        buyBtn.textContent = originalText;
        alert('Sorry, could not proceed to checkout. Please try again.');
      }
      return;
    }

    const btn = this.form.querySelector('[name="add"]');
    btn.disabled = true;
    btn.dataset.originalText = btn.textContent;
    btn.textContent = 'Adding…';

    const formData = new FormData(this.form);

    try {
      const res = await fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      });
      if (!res.ok) throw new Error(await res.text());

      // Refresh content in-place, then open (drawer was closed when ATC was clicked)
      await document.querySelector('cart-drawer')?.refresh();
      document.querySelector('cart-drawer')?.open();
      announceCart('Item added to your bag.');
      document.dispatchEvent(new CustomEvent('cart:item-added'));
    } catch (err) {
      console.error('[cart] add failed', err);
      alert('Sorry, we could not add that to your bag. Please try again or message us on WhatsApp.');
    } finally {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalText;
    }
  }
}
customElements.define('product-form', ProductForm);

// Persist cart note (measurements / instructions) on blur
document.addEventListener('blur', (e) => {
  if (!e.target.matches('[data-cart-note]')) return;
  fetch(window.theme.routes.cartUpdate, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: e.target.value })
  });
}, true);

// Cart line item quantity + remove controls
document.addEventListener('click', async (e) => {
  const inc = e.target.closest('[data-cart-increment]');
  const dec = e.target.closest('[data-cart-decrement]');
  const rem = e.target.closest('[data-cart-remove]');
  if (!inc && !dec && !rem) return;

  const btn = inc || dec || rem;
  btn.disabled = true;

  let key, quantity;

  if (rem) {
    key = rem.dataset.cartRemove;
    quantity = 0;
  } else {
    key = btn.dataset.cartIncrement || btn.dataset.cartDecrement;
    const current = parseInt(
      document.querySelector(`[data-cart-item="${key}"] .cart-item__controls span`)?.textContent ?? '1', 10
    );
    quantity = inc ? current + 1 : Math.max(0, current - 1);
  }

  // Show per-item spinner immediately
  const itemEl = document.querySelector(`[data-cart-item="${key}"]`);
  if (itemEl) itemEl.classList.add('is-loading');

  try {
    await fetch(window.theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    // Refresh inner content in-place — drawer stays open, no close/reopen flicker
    await document.querySelector('cart-drawer')?.refresh();
    announceCart(quantity === 0 ? 'Item removed from bag.' : 'Bag updated.');
  } catch (err) {
    console.error('[cart] update failed', err);
    // Remove spinner on error so the user can retry
    if (itemEl) itemEl.classList.remove('is-loading');
    btn.disabled = false;
  }
});

